import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Card,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Badge,
  Avatar,
  IconButton,
  TextField,
  Grid,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Stack,
  Tooltip,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Fab,
  Skeleton,
  Alert,
} from "@mui/material";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import {
  SupervisorAccount as SupervisorAccountIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Phone as PhoneIcon,
  Cake as CakeIcon,
} from "@mui/icons-material";

import apiFetch from "services/http";
import { useSeason } from "context/SeasonContext";
import { PageToolbar, ModalShell, ConfirmDialog, EmptyState, useNotify } from "components/Convokko";
import { POSICIONES } from "config";

const jugadorVacio = {
  _id: null,
  nombre: "",
  posicion: "",
  numero: "",
  fechaNacimiento: "",
  activo: true,
  lesionado: false,
  email: "",
  dni: "",
  telefono: "",
  telefono_familiar: [],
  equipo_actual: null,
};

const getInitials = (nombre = "") => `${nombre?.[0] || ""}`.toUpperCase();

const calculateAge = (dob) => {
  if (!dob) return null;
  const birth = new Date(dob);
  if (Number.isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
};

// Metadata como texto plano; el color queda reservado para estados (lesionado)
const renderChips = (jugador, compact = false) => {
  const age = calculateAge(jugador.fecha_nacimiento);
  const partes = [
    jugador.posicion || "Sin posición",
    age !== null ? `${age} años` : null,
    jugador.numero != null && jugador.numero !== "" ? `#${jugador.numero}` : null,
  ].filter(Boolean);
  return (
    <>
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={600}
        fontSize={compact ? "0.72rem" : "0.78rem"}
      >
        {partes.join(" · ")}
      </Typography>
      {jugador.lesionado && (
        <Chip
          label="Lesionado"
          size="small"
          color="error"
          sx={compact ? { fontSize: "0.7rem", height: 20 } : { fontWeight: 600 }}
        />
      )}
    </>
  );
};

// Lista/rejilla de jugadores aislada en su propio componente memoizado: puede
// haber cientos de jugadores, y no debe reconciliarse en cada tecla que se
// escribe en el formulario de crear/editar jugador (que vive en el padre).
const JugadoresLista = React.memo(function JugadoresLista({
  jugadores,
  viewMode,
  equiposById,
  onEdit,
  onDelete,
}) {
  const getEquipoName = (jugador) => {
    if (!jugador.equipo_actual) return "Sin equipo";
    return (
      jugador.nombre_equipo_actual ||
      equiposById[jugador.equipo_actual]?.nombre ||
      "Equipo desconocido"
    );
  };

  if (viewMode === "card") {
    return (
      <Grid container spacing={3}>
        {jugadores.map((jugador) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={jugador._id}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid",
                borderColor: "grey.200",
                height: "100%",
                transition: "all .2s ease",
                opacity: jugador.activo ? 1 : 0.65,
                "&:hover": {
                  borderColor: "primary.main",
                },
              }}
            >
              <Box p={2.5}>
                <Stack alignItems="center" spacing={1.5}>
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    badgeContent={
                      <Box
                        sx={{
                          width: 13,
                          height: 13,
                          borderRadius: "50%",
                          bgcolor: jugador.activo ? "success.main" : "grey.400",
                          border: "2px solid #fff",
                        }}
                      />
                    }
                  >
                    <Avatar
                      sx={{
                        width: 56,
                        height: 56,
                        fontSize: "1.3rem",
                        fontWeight: 700,
                        bgcolor: "rgba(245,158,11,0.15)",
                        color: "#B45309",
                      }}
                    >
                      {getInitials(jugador.nombre)}
                    </Avatar>
                  </Badge>

                  <Box textAlign="center" sx={{ minWidth: 0, width: "100%" }}>
                    <Typography variant="h6" fontWeight={700} noWrap lineHeight={1.25}>
                      {jugador.nombre}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap display="block">
                      {getEquipoName(jugador)}
                    </Typography>
                  </Box>

                  <Stack
                    direction="row"
                    spacing={0.75}
                    flexWrap="wrap"
                    useFlexGap
                    justifyContent="center"
                  >
                    {renderChips(jugador)}
                  </Stack>

                  <Stack direction="row" spacing={1}>
                    <Tooltip title="Editar">
                      <IconButton
                        size="small"
                        onClick={() => onEdit(jugador)}
                        sx={{ color: "text.secondary", "&:hover": { color: "info.main" } }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton
                        size="small"
                        onClick={() => onDelete(jugador)}
                        sx={{ color: "text.secondary", "&:hover": { color: "error.main" } }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: "1px solid",
        borderColor: "grey.200",
        overflow: "hidden",
      }}
    >
      <List disablePadding>
        {jugadores.map((jugador, index) => (
          <ListItem
            key={jugador._id}
            divider={index < jugadores.length - 1}
            sx={{ py: 1, px: { xs: 2, sm: 3 }, "&:hover": { bgcolor: "grey.100" } }}
          >
            <ListItemAvatar>
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                badgeContent={
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      bgcolor: jugador.activo ? "success.main" : "grey.400",
                      border: "2px solid #fff",
                    }}
                  />
                }
              >
                <Avatar
                  sx={{
                    width: 44,
                    height: 44,
                    fontWeight: 700,
                    bgcolor: "rgba(245,158,11,0.15)",
                    color: "#B45309",
                  }}
                >
                  {getInitials(jugador.nombre)}
                </Avatar>
              </Badge>
            </ListItemAvatar>
            <ListItemText
              disableTypography
              primary={
                <Typography variant="subtitle2" fontWeight={700}>
                  {jugador.nombre}
                </Typography>
              }
              secondary={
                <Stack spacing={0.5} mt={0.25}>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    {renderChips(jugador, true)}
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {getEquipoName(jugador)}
                  </Typography>
                </Stack>
              }
            />
            <ListItemSecondaryAction>
              <Stack direction="row" spacing={0.5}>
                <IconButton
                  size="small"
                  onClick={() => onEdit(jugador)}
                  sx={{ color: "text.secondary", "&:hover": { color: "info.main" } }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => onDelete(jugador)}
                  sx={{ color: "text.secondary", "&:hover": { color: "error.main" } }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>
    </Paper>
  );
});

JugadoresLista.propTypes = {
  jugadores: PropTypes.array.isRequired,
  viewMode: PropTypes.string.isRequired,
  equiposById: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default function JugadoresDashboard() {
  const notify = useNotify();
  const { temporada } = useSeason();

  const [jugadores, setJugadores] = useState([]);
  const [equipos, setEquipos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [equipoFiltro, setEquipoFiltro] = useState("");
  const [posicionFiltro, setPosicionFiltro] = useState("");
  const [statusFiltro, setStatusFiltro] = useState("");
  // Vista lista por defecto (más densa); se recuerda la preferencia del usuario
  const [viewMode, setViewMode] = useState(
    () => localStorage.getItem("convokko_vista_jugadores") || "list"
  );

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    localStorage.setItem("convokko_vista_jugadores", mode);
  };

  const [openModal, setOpenModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentJugador, setCurrentJugador] = useState(jugadorVacio);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const [dataJugadores, dataEquipos] = await Promise.all([
        apiFetch("/api/jugadores/ver-jugadores-equipo"),
        apiFetch("/api/equipos"),
      ]);
      setJugadores(Array.isArray(dataJugadores) ? dataJugadores : []);
      setEquipos(Array.isArray(dataEquipos) ? dataEquipos : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const equiposById = useMemo(() => {
    const map = {};
    equipos.forEach((e) => {
      map[e._id || e.id] = e;
    });
    return map;
  }, [equipos]);

  const equiposTemporada = useMemo(
    () => equipos.filter((e) => Number(e.temporada) === Number(temporada)),
    [equipos, temporada]
  );

  const getEquipoName = (jugador) => {
    if (!jugador.equipo_actual) return "Sin equipo";
    return (
      jugador.nombre_equipo_actual ||
      equiposById[jugador.equipo_actual]?.nombre ||
      "Equipo desconocido"
    );
  };

  // Coherencia con la temporada global: se ocultan solo los jugadores cuyo
  // equipo pertenece con seguridad a OTRA temporada. Sin equipo => siempre visibles.
  const filteredJugadores = useMemo(
    () =>
      jugadores
        .filter((jugador) => {
          const equipo = jugador.equipo_actual ? equiposById[jugador.equipo_actual] : null;
          if (equipo && Number(equipo.temporada) !== Number(temporada)) return false;
          if (equipoFiltro && jugador.equipo_actual !== equipoFiltro) return false;
          if (
            posicionFiltro &&
            (jugador.posicion || "").toLowerCase() !== posicionFiltro.toLowerCase()
          )
            return false;
          if (statusFiltro === "Activo" && !jugador.activo) return false;
          if (statusFiltro === "Inactivo" && jugador.activo) return false;
          if (searchTerm) {
            const q = searchTerm.toLowerCase();
            const target = `${jugador.nombre || ""} ${jugador.posicion || ""} ${
              jugador.numero || ""
            } ${jugador.dni || ""} ${getEquipoName(jugador)}`.toLowerCase();
            if (!target.includes(q)) return false;
          }
          return true;
        })
        .sort((a, b) => (a.nombre || "").localeCompare(b.nombre || "", "es")),
    [jugadores, equiposById, temporada, equipoFiltro, posicionFiltro, statusFiltro, searchTerm]
  );

  // --- CRUD ---
  const handleOpenCreate = () => {
    setEditMode(false);
    setCurrentJugador(jugadorVacio);
    setOpenModal(true);
  };

  // useCallback: se pasa como prop a JugadoresLista (memoizado), necesita
  // identidad estable para que el memo evite reconciliar la lista al escribir
  // en el formulario.
  const handleOpenEdit = useCallback((jugador) => {
    setEditMode(true);
    setCurrentJugador({
      _id: jugador._id,
      nombre: jugador.nombre || "",
      posicion: jugador.posicion || "",
      numero: jugador.numero || "",
      fechaNacimiento: jugador.fecha_nacimiento || "",
      activo: Boolean(jugador.activo),
      lesionado: Boolean(jugador.lesionado),
      email: jugador.email || "",
      dni: jugador.dni || "",
      telefono: jugador.telefono || "",
      telefono_familiar: Array.isArray(jugador.telefono_familiar) ? jugador.telefono_familiar : [],
      equipo_actual: jugador.equipo_actual || null,
    });
    setOpenModal(true);
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCurrentJugador((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = {
        nombre: currentJugador.nombre,
        posicion: currentJugador.posicion,
        numero: currentJugador.numero ? parseInt(currentJugador.numero, 10) : null,
        fecha_nacimiento: currentJugador.fechaNacimiento || null,
        dni: currentJugador.dni || null,
        activo: currentJugador.activo,
        lesionado: currentJugador.lesionado,
        email: currentJugador.email || null,
        telefono: currentJugador.telefono || null,
        telefono_familiar: currentJugador.telefono_familiar?.length
          ? currentJugador.telefono_familiar
          : null,
        equipo_actual: currentJugador.equipo_actual || null,
      };
      if (editMode) {
        await apiFetch("/api/jugadores/actualizar-jugador-equipo", {
          method: "PUT",
          body: { ...payload, _id: currentJugador._id },
        });
        notify.success("Jugador actualizado");
      } else {
        await apiFetch("/api/jugadores/crear-jugador-equipo", { method: "POST", body: payload });
        notify.success("Jugador creado");
      }
      setOpenModal(false);
      await fetchData();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setSaving(true);
      await apiFetch("/api/jugadores/eliminar-jugador-equipo", {
        method: "DELETE",
        params: { jugador_id: deleteTarget._id },
      });
      setJugadores((prev) => prev.filter((j) => j._id !== deleteTarget._id));
      notify.success("Jugador eliminado");
      setDeleteTarget(null);
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const renderSkeleton = () => (
    <Grid container spacing={3}>
      {[...Array(8)].map((_, i) => (
        <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
          <Card sx={{ p: 3, borderRadius: 3 }}>
            <Stack alignItems="center" spacing={2}>
              <Skeleton variant="circular" width={56} height={56} />
              <Skeleton variant="text" width="75%" height={24} />
              <Skeleton variant="rectangular" width="100%" height={28} sx={{ borderRadius: 2 }} />
            </Stack>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box py={3}>
        <PageToolbar
          icon={SupervisorAccountIcon}
          title="Jugadores"
          count={filteredJugadores.length}
          countLabel={filteredJugadores.length === 1 ? "jugador" : "jugadores"}
          action={{
            label: "Nuevo jugador",
            shortLabel: "Nuevo",
            icon: <AddIcon />,
            onClick: handleOpenCreate,
          }}
          search={{
            value: searchTerm,
            onChange: setSearchTerm,
            placeholder: "Buscar por nombre, posición, dorsal, DNI o equipo...",
          }}
          filters={[
            {
              id: "equipo",
              label: "Equipo",
              allLabel: "Todos los equipos",
              value: equipoFiltro,
              onChange: setEquipoFiltro,
              options: equiposTemporada.map((e) => ({ value: e._id || e.id, label: e.nombre })),
              minWidth: 130,
            },
            {
              id: "posicion",
              label: "Posición",
              allLabel: "Todas las posiciones",
              value: posicionFiltro,
              onChange: setPosicionFiltro,
              options: POSICIONES.map((p) => ({ value: p, label: p })),
              minWidth: 115,
            },
            {
              id: "estado",
              label: "Estado",
              allLabel: "Todos los estados",
              value: statusFiltro,
              onChange: setStatusFiltro,
              options: [
                { value: "Activo", label: "Activos" },
                { value: "Inactivo", label: "Inactivos" },
              ],
              minWidth: 100,
            },
          ]}
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {loading ? (
          renderSkeleton()
        ) : filteredJugadores.length === 0 ? (
          <EmptyState
            icon={SupervisorAccountIcon}
            title="No hay jugadores"
            subtitle={
              jugadores.length === 0
                ? "Aún no tienes jugadores registrados."
                : "Ningún jugador coincide con los filtros o la temporada seleccionada."
            }
            actionLabel="Añadir jugador"
            onAction={handleOpenCreate}
          />
        ) : (
          <JugadoresLista
            jugadores={filteredJugadores}
            viewMode={viewMode}
            equiposById={equiposById}
            onEdit={handleOpenEdit}
            onDelete={setDeleteTarget}
          />
        )}
      </Box>

      <Fab
        color="primary"
        aria-label="Añadir jugador"
        onClick={handleOpenCreate}
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          color: "#fff",
          display: { xs: "flex", sm: "none" },
        }}
      >
        <AddIcon />
      </Fab>

      {/* Crear / editar jugador */}
      <ModalShell
        open={openModal}
        onClose={() => setOpenModal(false)}
        title={editMode ? "Editar jugador" : "Nuevo jugador"}
        subtitle={editMode ? currentJugador.nombre : "Datos del jugador"}
        icon={SupervisorAccountIcon}
        actions={
          <>
            <Button onClick={() => setOpenModal(false)} sx={{ color: "text.secondary" }}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              startIcon={<SaveIcon />}
              disabled={saving || !currentJugador.nombre || !currentJugador.posicion}
              sx={{ borderRadius: 2 }}
            >
              {saving ? "Guardando..." : editMode ? "Guardar cambios" : "Crear jugador"}
            </Button>
          </>
        }
      >
        <Stack spacing={2.5} mt={0.5}>
          <TextField
            label="Nombre completo"
            name="nombre"
            value={currentJugador.nombre}
            onChange={handleChange}
            fullWidth
            required
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                value={currentJugador.posicion || null}
                onChange={(e, v) => setCurrentJugador((prev) => ({ ...prev, posicion: v || "" }))}
                options={POSICIONES}
                renderInput={(params) => <TextField {...params} label="Posición" required />}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Dorsal"
                name="numero"
                type="number"
                value={currentJugador.numero}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Nacimiento"
                name="fechaNacimiento"
                type="date"
                value={currentJugador.fechaNacimiento}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
          <FormControl fullWidth>
            <InputLabel>Equipo</InputLabel>
            <Select
              label="Equipo"
              value={currentJugador.equipo_actual || ""}
              onChange={(e) =>
                setCurrentJugador((prev) => ({ ...prev, equipo_actual: e.target.value || null }))
              }
              sx={{ height: 44 }}
            >
              <MenuItem value="">Sin equipo</MenuItem>
              {equiposTemporada.map((eq) => (
                <MenuItem key={eq._id} value={eq._id}>
                  {eq.nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="DNI"
                name="dni"
                value={currentJugador.dni}
                onChange={handleChange}
                fullWidth
                placeholder="12345678A"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Teléfono"
                name="telefono"
                value={currentJugador.telefono}
                onChange={handleChange}
                fullWidth
                placeholder="600000000"
              />
            </Grid>
          </Grid>
          <TextField
            label="Email"
            name="email"
            type="email"
            value={currentJugador.email}
            onChange={handleChange}
            fullWidth
          />
          <Autocomplete
            multiple
            freeSolo
            value={currentJugador.telefono_familiar}
            onChange={(e, v) => setCurrentJugador((prev) => ({ ...prev, telefono_familiar: v }))}
            options={[]}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  variant="outlined"
                  icon={<PhoneIcon />}
                  label={option}
                  {...getTagProps({ index })}
                  key={option}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Teléfonos familiares"
                placeholder="Escribe y pulsa Enter"
              />
            )}
          />
          <Stack direction="row" spacing={3}>
            <FormControlLabel
              control={
                <Checkbox checked={currentJugador.activo} onChange={handleChange} name="activo" />
              }
              label="Jugador activo"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={currentJugador.lesionado}
                  onChange={handleChange}
                  name="lesionado"
                  color="error"
                />
              }
              label="Lesionado"
            />
          </Stack>
        </Stack>
      </ModalShell>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Eliminar jugador"
        message={
          <>
            ¿Seguro que quieres eliminar a <b>{deleteTarget?.nombre}</b>? Esta acción no se puede
            deshacer.
          </>
        }
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
        loading={saving}
      />

      <Footer />
    </DashboardLayout>
  );
}
